<template>
  <span
    ref="input"
    role="spinbutton"
    :contenteditable="!disabled"
    :data-segment="'dayPeriod'"
    :data-placeholder="value === null"
    :tabindex="disabled ? -1 : 0"
    aria-label="AM/PM"
    :aria-valuenow="value ?? undefined"
    :aria-valuetext="displayText"
    :aria-valuemin="0"
    :aria-valuemax="1"
    :style="{ caretColor: 'transparent' }"
    inputmode="text"
    autocorrect="off"
    spellcheck="false"
    enterkeyhint="next"
    class="select-none rounded px-1 outline-none focus:bg-blue-500 focus:text-white"
    @focus="handleFocus"
    @keydown="handleKeydown"
    @beforeinput="handleBeforeInput"
    >{{ displayText }}</span
  >
</template>

<script setup lang="ts">
import { computed, useTemplateRef } from "vue";

const props = defineProps<{
  value: number | null;
  amLabel: string;
  pmLabel: string;
  disabled?: boolean;
}>();

const emit = defineEmits<{
  change: [value: number];
  next: [];
  prev: [];
}>();

const input = useTemplateRef("input");

const displayText = computed(() => {
  if (props.value === null) {
    return "––";
  }
  return props.value === 0 ? props.amLabel : props.pmLabel;
});

const focus = (): void => {
  input.value?.focus();
};

const handleFocus = (): void => {
  const selection = window.getSelection();
  if (selection && input.value) {
    selection.collapse(input.value);
  }
};

const handleKeydown = (event: KeyboardEvent): void => {
  if (props.disabled) {
    return;
  }

  switch (event.key) {
    case "ArrowLeft": {
      event.preventDefault();
      emit("prev");
      break;
    }
    case "ArrowRight": {
      event.preventDefault();
      emit("next");
      break;
    }
    case "ArrowUp":
    case "ArrowDown": {
      event.preventDefault();
      // Toggle AM/PM
      emit("change", props.value === 1 ? 0 : 1);
      break;
    }
  }
};

const handleBeforeInput = (event: Event): void => {
  event.preventDefault();

  if (props.disabled) {
    return;
  }

  const ev = event as InputEvent;
  const data = ev.data;
  if (!data) return;

  const char = data.toLowerCase();

  // Match typed character against locale AM/PM labels
  if (props.amLabel.toLowerCase().startsWith(char)) {
    emit("change", 0);
  } else if (props.pmLabel.toLowerCase().startsWith(char)) {
    emit("change", 1);
  }
};

defineExpose({
  focus,
  el: input,
});
</script>
